"use client";

import { Popup } from "@/app/components/popup";
import { IconStar, IconStarFilled } from "@tabler/icons-react";
import { useStore } from "@tanstack/react-store";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useCallback, useEffect, useState } from "react";
import { EditableText } from "../components/EditableText";
import { FoodCard } from "../components/foodcard";
import { fetchProduct, getProduct, productIndexStore } from "../stores/all-products";
import {
  addCustomization,
  customizationsStore,
  removeCustomization,
  withCustomizations,
} from "../stores/customizations-store";
import { setErrorToast } from "../stores/error-toast";
import {
  addFavoriteProduct,
  favoriteProductsIndexStore,
  removeFavoriteProduct,
} from "../stores/favorite-products";
import { getProductName, type ProductV3 } from "../types";

const ProductPopupContent = ({ onClose }: { onClose?: () => void }) => {
  const [popupHeight, setPopupHeight] = useState<number | undefined>(undefined);
  useStore(productIndexStore);
  useStore(customizationsStore);
  const router = useRouter();
  const searchParams = useSearchParams();
  const productCode = searchParams.get("product");
  const favoriteProductCodes = useStore(favoriteProductsIndexStore);

  useEffect(() => {
    if (productCode) {
      fetchProduct(productCode).catch((error) => {
        console.error("Failed to fetch product:", error);
        setErrorToast({ message: `Error loading product ${productCode}. ${error}` });
      });
    }
  }, [productCode]);

  const product = getProduct(productCode ?? "");
  const myProduct = product ? withCustomizations(product) : null;
  const isFavorite = favoriteProductCodes.has(myProduct?.code ?? "");
  const toggleFavorite = (e: React.MouseEvent, product: ProductV3) => {
    e.preventDefault();
    e.stopPropagation();

    if (favoriteProductCodes.has(product.code!)) {
      removeFavoriteProduct(product.code!);
    } else {
      addFavoriteProduct(product);
    }
  };

  const handleClose = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("product");
    router.push("?" + params, { scroll: false });
    onClose?.();
  }, [router, searchParams, onClose]);

  return (
    <Popup
      isOpen={!!myProduct?.code}
      onClose={handleClose}
      className={`
         bg-white dark:bg-zinc-900 max-w-md`}
      initialHeight={popupHeight !== undefined ? popupHeight : 95}
      onResize={setPopupHeight}
      header={
        myProduct && product ? (
          <section>
            <div className="flex justify-between items-start">
              <div className="flex-grow">
                <div>
                  <EditableText
                    value={getProductName(myProduct!)}
                    onChange={(newName) => {
                      if (!myProduct?.code) return;
                      if (!newName || newName === getProductName(product!)) {
                        removeCustomization(myProduct.code, { product_name: undefined });
                      } else {
                        addCustomization(myProduct.code, { product_name: newName });
                      }
                    }}
                    className="mr-2 font-semibold capitalize text-xl cursor-pointer"
                  />
                </div>
              </div>
              <button
                onClick={(e) => toggleFavorite(e, myProduct)}
                className="rounded-full transition-colors mt-1"
              >
                {isFavorite ? (
                  <IconStarFilled className="size-5 text-yellow-400" />
                ) : (
                  <IconStar className="size-5 text-gray-500 dark:text-gray-400" />
                )}
              </button>
            </div>

            <div className="opacity-70 flex items-center justify-start flex-wrap whitespace-pre">
              {getProductName(myProduct) !== getProductName(product) && (
                <span className="text-sm text-gray-900 dark:text-white capitalize">
                  {getProductName(product)}
                  {", "}
                </span>
              )}
              {myProduct.brands && (
                <EditableText
                  value={myProduct.brands}
                  onChange={(newValue) => {
                    if (newValue.trim() === "") {
                      removeCustomization(myProduct.code!, { brands: undefined });
                    } else {
                      addCustomization(myProduct.code!, { brands: newValue });
                    }
                  }}
                  className="text-sm text-gray-900 dark:text-white capitalize"
                >
                  {myProduct.brands}
                </EditableText>
              )}
              {myProduct.brands && myProduct.quantity && ", "}
              {myProduct.quantity && (
                <EditableText
                  value={myProduct.quantity.replaceAll(" ", "")}
                  onChange={(newValue) => {
                    if (newValue.trim() === "") {
                      removeCustomization(myProduct.code!, { quantity: undefined });
                    } else {
                      addCustomization(myProduct.code!, { quantity: newValue });
                    }
                  }}
                  className="text-sm text-gray-900 dark:text-white"
                />
              )}
            </div>
          </section>
        ) : null
      }
    >
      {product && <FoodCard product={product} />}
    </Popup>
  );
};

export const ProductPopup = (props: { onClose?: () => void }) => {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ProductPopupContent {...props} />
    </Suspense>
  );
};
